import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import authService from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
`;

const Message = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`;

const ErrorMessage = styled.div`
  background: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  color: #d32f2f;
  font-size: 14px;
`;

const RetryButton = styled.button`
  margin-top: 24px;
  padding: 12px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #5568d3;
  }
`;

/**
 * Auth Callback Page
 * Handles OAuth redirect from backend
 * Extracts token from URL and stores it
 */
const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Parse URL parameters
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const error = params.get('error');

        // Check for errors
        if (error) {
          setError(decodeURIComponent(error));
          return;
        }

        // Check for token
        if (!token) {
          setError('No authentication token received. Please try again.');
          return;
        }

        // Store token
        authService.setToken(token);

        // Refresh user data in context
        await refreshUser();

        // Get the redirect URL from session storage (if set before login)
        const redirectTo = sessionStorage.getItem('auth_redirect') || '/';
        sessionStorage.removeItem('auth_redirect');

        // Success! Redirect to original destination or home
        // Use window.location.href to force full page reload and refresh authenticated state
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 1000);
      } catch (err) {
        console.error('Error handling auth callback:', err);
        setError('An error occurred during authentication. Please try again.');
      }
    };

    handleCallback();
  }, [location, navigate, refreshUser]);

  const handleRetry = () => {
    authService.loginWithGoogle();
  };

  if (error) {
    return (
      <Container>
        <Card>
          <Title>Authentication Failed</Title>
          <ErrorMessage>{error}</ErrorMessage>
          <RetryButton onClick={handleRetry}>Try Again</RetryButton>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Spinner />
        <Title>Completing Login</Title>
        <Message>Please wait while we log you in...</Message>
      </Card>
    </Container>
  );
};

export default AuthCallbackPage;
